import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, spacing } from 'component-library';
import { MetaInfo } from '@sr/types/versionTracking';
import { Modal } from 'component-library/components/Utils/Modal';
import { S3_ASSETS_URL } from '#lib/utils/environment';

let activeVersion: MetaInfo | null = null;

const fetchMetaInfo = async (): Promise<MetaInfo> => {
  const url = `${S3_ASSETS_URL}/meta/meta.json`;

  const ignoreCacheHeaders = {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
  };

  const fetchOptions: RequestInit = {
    method: 'GET',
    headers: new Headers(ignoreCacheHeaders),
  };

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const error = new Error('Network response was not ok');
    const responseBody = await response
      .clone()
      .text()
      .catch(() => 'Backend responded with non-ok status.');

    Sentry.captureException(error, {
      extra: {
        url,
        status: response.status,
        statusText: response.statusText,
        method: fetchOptions.method,
        body: fetchOptions.body,
        responseBody,
        headers: Object.fromEntries(new Headers(fetchOptions.headers).entries()),
      },
      tags: {
        fetch_error: true,
      },
    });

    throw error;
  }

  const data: unknown = await response.json();

  return MetaInfo.parse(data);
};

export const VersionTracking = () => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setInterval(() => {
      void (async () => {
        const data = await fetchMetaInfo();

        if (!activeVersion) {
          activeVersion = {
            version: data.version,
            releaseDate: data.releaseDate,
          };
        } else {
          const activeVersionDate = dayjs(activeVersion.releaseDate);
          const currentVersionDate = dayjs(data.releaseDate);

          if (!currentVersionDate.isValid()) {
            console.warn('Invalid release date:', data.releaseDate);

            return;
          }

          if (activeVersionDate.isBefore(currentVersionDate)) {
            setShowModal(true);
          }
        }
      })();
    }, 60000); // Fetch every minute

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Modal
      open={showModal}
      disableClose={true}
      primaryAction={{
        label: t('version-tracking.modal.reload-button'),
        action: () => {
          location.reload();
        },
      }}
    >
      <Heading as="h2" variant="large" mb={spacing.xsmall}>
        {t('version-tracking.modal.title')}
      </Heading>

      <Paragraph variant="large">{t('version-tracking.modal.text')}</Paragraph>
    </Modal>
  );
};
